import { supabase } from '@/lib/supabase';

export interface PropBotI18nContent {
  bot_id: string;
  language_code: string;
  description?: string;
  features?: string[];
  requirements?: string[];
  challenge_rules?: string[];
  is_original_language: boolean;
  is_ai_translated: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Fetch multilingual content for a prop bot
 */
export const fetchPropBotI18nContent = async (
  botId: string, 
  languageCode: string
): Promise<PropBotI18nContent | null> => {
  try {
    // First try to get content for the requested language
    const { data, error } = await supabase
      .from('bot_prop_details_i18n')
      .select('*')
      .eq('bot_id', botId)
      .eq('language_code', languageCode)
      .single();

    if (!error && data) {
      return data;
    }

    // If not found and not requesting original language, try to get original language content
    if (languageCode !== 'en') {
      const { data: originalData, error: originalError } = await supabase
        .from('bot_prop_details_i18n')
        .select('*')
        .eq('bot_id', botId)
        .eq('is_original_language', true)
        .single();

      if (!originalError && originalData) {
        return originalData;
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching i18n content:', error);
    return null;
  }
};

/**
 * Get all available languages for a bot with translation timestamps
 */
export const fetchPropBotAvailableLanguages = async (botId: string) => {
  try {
    const { data, error } = await supabase
      .from('bot_prop_details_i18n')
      .select('language_code, is_original_language, is_ai_translated, updated_at')
      .eq('bot_id', botId)
      .order('language_code');

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching available languages:', error);
    return [];
  }
};

/**
 * Save or update multilingual content
 */
export const savePropBotI18nContent = async (content: PropBotI18nContent) => {
  try {
    const { error } = await supabase
      .from('bot_prop_details_i18n')
      .upsert({
        ...content,
        is_ai_translated: false, // Manual edit, not AI translated
      }, {
        onConflict: 'bot_id,language_code'
      });

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error saving i18n content:', error);
    throw error;
  }
};

/**
 * Get content status for language selector
 */
export const getPropBotLanguageStatus = async (botId: string) => {
  try {
    const availableLanguages = await fetchPropBotAvailableLanguages(botId);
    
    const languageStatus = {
      'en': { available: false, isOriginal: false, isAiTranslated: false },
      'vi': { available: false, isOriginal: false, isAiTranslated: false },
      'zh': { available: false, isOriginal: false, isAiTranslated: false },
      'ja': { available: false, isOriginal: false, isAiTranslated: false },
      'ko': { available: false, isOriginal: false, isAiTranslated: false },
      'es': { available: false, isOriginal: false, isAiTranslated: false },
    };

    availableLanguages.forEach(lang => {
      if (languageStatus[lang.language_code as keyof typeof languageStatus]) {
        languageStatus[lang.language_code as keyof typeof languageStatus] = {
          available: true,
          isOriginal: lang.is_original_language,
          isAiTranslated: lang.is_ai_translated,
        };
      }
    });

    return languageStatus;
  } catch (error) {
    console.error('Error getting language status:', error);
    return {};
  }
};